export type DeoverlapFlow = 'down' | 'up'

export type DeoverlapEntry = {
  id: string
  top: number
  height: number
  isTarget: boolean
}

export function resolveDeoverlapPositions(
  entries: DeoverlapEntry[],
  spacingUnits: number,
  flow: DeoverlapFlow
): Map<string, number> {
  const ordered = [...entries].sort((a, b) => {
    return flow === 'down' ? a.top - b.top : b.top - a.top
  })
  const updates = new Map<string, number>()
  let cursor: number | undefined

  ordered.forEach((entry) => {
    const top = entry.top
    const bottom = top + entry.height

    if (!entry.isTarget) {
      if (cursor !== undefined) {
        cursor = flow === 'down'
          ? Math.max(cursor, bottom + spacingUnits)
          : Math.min(cursor, top - spacingUnits)
      }
      return
    }

    if (cursor === undefined) {
      cursor = flow === 'down'
        ? bottom + spacingUnits
        : top - spacingUnits
      return
    }

    const nextTop = flow === 'down'
      ? cursor
      : cursor - entry.height
    updates.set(entry.id, nextTop)
    cursor = flow === 'down'
      ? nextTop + entry.height + spacingUnits
      : nextTop - spacingUnits
  })

  return updates
}

export function resolveSoftDeoverlapPositions(
  entries: DeoverlapEntry[],
  flow: DeoverlapFlow
): Map<string, number> {
  const ordered = [...entries].sort((a, b) => {
    return flow === 'down' ? a.top - b.top : b.top - a.top
  })
  const updates = new Map<string, number>()
  let cursor: number | undefined

  ordered.forEach((entry) => {
    const top = entry.top
    const bottom = top + entry.height

    if (cursor === undefined) {
      cursor = flow === 'down' ? bottom : top
      return
    }

    const overlaps = flow === 'down'
      ? top < cursor
      : bottom > cursor
    const nextTop = overlaps
      ? flow === 'down'
        ? cursor
        : cursor - entry.height
      : top

    if (entry.isTarget) {
      if (nextTop !== top) {
        updates.set(entry.id, nextTop)
      }
      cursor = flow === 'down'
        ? Math.max(cursor, nextTop + entry.height)
        : Math.min(cursor, nextTop)
      return
    }

    cursor = flow === 'down'
      ? Math.max(cursor, bottom)
      : Math.min(cursor, top)
  })

  return updates
}

export function resolveClusteredSoftDeoverlapPositions(
  entries: DeoverlapEntry[]
): Map<string, number> {
  const updates = new Map<string, number>()
  forEachOverlapCluster(entries, (cluster) => {
    const flow = resolveClusterFlow(cluster)
    resolveSoftDeoverlapPositions(cluster, flow).forEach((top, id) => {
      updates.set(id, top)
    })
  })

  return updates
}

export function resolveClusteredSpacingPositions(
  entries: DeoverlapEntry[],
  spacingUnits: number
): Map<string, number> {
  const updates = new Map<string, number>()
  forEachOverlapCluster(entries, (cluster) => {
    const flow = resolveClusterFlow(cluster)
    resolveDeoverlapPositions(cluster, spacingUnits, flow).forEach((top, id) => {
      updates.set(id, top)
    })
  })

  return updates
}

export function resolveStackSpacingPositions(
  entries: DeoverlapEntry[],
  spacingUnits: number
): Map<string, number> {
  if (!entries.length) return new Map()
  return resolveDeoverlapPositions(entries, spacingUnits, resolveClusterFlow(entries))
}

function forEachOverlapCluster(
  entries: DeoverlapEntry[],
  visit: (cluster: DeoverlapEntry[]) => void
): void {
  const ordered = [...entries].sort((a, b) => a.top - b.top)
  let cluster: DeoverlapEntry[] = []
  let clusterBottom: number | undefined

  const flushCluster = () => {
    if (!cluster.length) return
    visit(cluster)
    cluster = []
    clusterBottom = undefined
  }

  ordered.forEach((entry) => {
    const bottom = entry.top + entry.height
    if (!cluster.length) {
      cluster = [entry]
      clusterBottom = bottom
      return
    }
    if (clusterBottom !== undefined && entry.top < clusterBottom) {
      cluster.push(entry)
      clusterBottom = Math.max(clusterBottom, bottom)
      return
    }
    flushCluster()
    cluster = [entry]
    clusterBottom = bottom
  })
  flushCluster()
}

function resolveClusterFlow(entries: DeoverlapEntry[]): DeoverlapFlow {
  const top = Math.min(...entries.map(entry => entry.top))
  const bottom = Math.max(...entries.map(entry => entry.top + entry.height))
  if (bottom <= 0) return 'up'
  if (top >= 0) return 'down'
  const center = (top + bottom) / 2
  return center < 0 ? 'up' : 'down'
}
